import { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, CircularProgress, Divider, Grid2, IconButton, TextField, Typography } from "@mui/material";
import { Close, Send } from "@mui/icons-material";
// import socket from "../../../socket";
import { useAuth } from "../hooks/useAuth";
import api from "../utils/axios";
import { IOrderMessage } from "sierra";
import socket from "../utils/socket";
import { useNavigate } from "react-router-dom";
import OrderChatSkeleton from "./Skeletons/OrderChatSkeleton";
import { useApp } from "../contexts/AppContext";

export default function OrderChat({ order_id }: { order_id: number }) {
    const { user, userLoaded } = useAuth()
    const { translate, lang } = useApp()

    const [messages, setMessages] = useState<Required<IOrderMessage>[]>();
    const [message, setMessage] = useState('');
    const [apiCalling, setApiCalling] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!userLoaded || !user) return
        fetchMessages()
        socket.on("orders_messages_changed", fetchMessages);
        return () => {
            socket.off("orders_messages_changed", fetchMessages);
        }
    }, [userLoaded, user, order_id])

    useEffect(() => {
        markAsRead()
    }, [messages])

    useEffect(() => {
        // Scroll to the bottom when messages change
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [messages]);

    const fetchMessages = (data?: Array<{ message_id: number, order_id: number }>) => {
        if (data && !data.find(d => d.order_id === order_id)) return;
        api.get('/ordersChat/' + order_id).then(res => setMessages(res.data)).catch(console.error)
    }

    const markAsRead = () => {
        api.patch(`/ordersChat/${order_id}/messageRead`).catch(console.error)
    }

    const apiCall = () => {
        setApiCalling(true)
        api.post('/ordersChat', { message: message, order_id: order_id }).then(() => {
            setMessage('')
        }).catch(console.error).finally(() => setApiCalling(false))
    }

    // yes i have shamelessly copied this function from gpt
    const mergeMessages = useCallback((messages: Required<IOrderMessage>[]) => {
        const mergedMessages: Required<IOrderMessage>[] = [];

        // Sort messages by creation_timestamp in ascending order
        const sortedMessages = [...messages].sort((a, b) =>
            new Date(a.creation_timestamp).getTime() - new Date(b.creation_timestamp).getTime()
        );

        let acc: Required<IOrderMessage> | null = null; // Initialize the accumulator

        sortedMessages.forEach((curr) => {
            // Check if the current message's sender is the same as the previous message's sender
            if (acc && acc.sender_id === curr.sender_id) {
                // Merge the messages with a newline separator
                acc.message += `\n${curr.message}`;
            } else {
                // Push the accumulated message to the result array if it exists
                if (acc) mergedMessages.push(acc);
                // Start a new merge with the current message
                acc = { ...curr };
            }
        });

        // Push the last accumulated message if it exists
        if (acc) mergedMessages.push(acc);

        return mergedMessages;
    }, []);


    if (!order_id) return <Typography color="error">Order ID is required</Typography>

    if (!messages || !user) return <OrderChatSkeleton />

    return (
        <Grid2 container flexDirection={'column'} height={'100%'} gap={1}>
            <Grid2 container size='grow' display={'flex'} overflow={'auto'} ref={containerRef} flexDirection={'column'} wrap="nowrap" gap={1} paddingRight={1}>
                <Grid2>
                    <Typography variant="caption" sx={{ fontStyle: 'italic', whiteSpace: 'pre-line' }}>{translate("This is the beginning of your chat for order")} # {order_id}{messages.length === 0 && `\n${translate("Send a message")}!`}</Typography>
                </Grid2>
                {mergeMessages(messages).map((m, i) =>
                    <Grid2 container key={i} display={'flex'} flexDirection={m.sender_id === user.user_id ? 'row-reverse' : 'row'} alignItems={'start'} wrap="nowrap" gap={1} justifyContent={m.sender_id === user?.user_id ? lang === 'ar' ? 'left' : 'right' : lang === 'ar' ? 'right' : 'left'}>
                        <Grid2>
                            {m.sender.avatar_url ? <img src={m.sender.avatar_url} style={{ borderRadius: 35, width: 35, height: 35 }} /> : <Avatar sx={{ width: 35, height: 35 }} />}
                        </Grid2>
                        <Grid2 container display={'flex'} flexDirection={'column'} justifyContent={'end'} sx={{
                            backgroundColor: m.sender_id == user.user_id ? 'primary.dark' : 'lightgrey',
                            color: m.sender_id == user.user_id ? 'primary.contrastText' : undefined,
                            display: 'inline-block',
                            maxWidth: '60%',
                            padding: 1,
                            borderRadius: 2
                        }}>
                            <Grid2 justifyContent={m.sender_id === user.user_id ? 'end' : 'start'} display={m.sender_id === user.user_id ? 'none' : 'flex'}>
                                <Typography variant="caption" color={m.sender_id === user.user_id ? "secondary" : "info"}>{m.sender.name}</Typography>
                            </Grid2>
                            <Grid2 justifyContent={'start'} display={'flex'}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{m.message}</Typography>
                            </Grid2>
                            <Grid2 justifyContent={'end'} display={'flex'}>
                                <Typography style={{ color: 'grey', fontSize: 12, direction: 'ltr' }}>
                                    {new Date(m.creation_timestamp).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0) ?
                                        new Date(m.creation_timestamp).toLocaleTimeString() : new Date(m.creation_timestamp).toLocaleString(lang === 'ar' ? 'ar-SD' : undefined)}
                                </Typography>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                )}
            </Grid2>
            <Grid2 size='auto'>
                <Divider orientation="horizontal" />
            </Grid2>
            <Grid2 container size='auto' display={'flex'} alignItems={'center'} gap={0.5} sx={{ direction: 'ltr' }}>
                <Grid2 size={'grow'} display={'flex'} alignItems={'center'} >
                    <TextField size='small' fullWidth value={message} onChange={(e) => setMessage(e.target.value)} disabled={apiCalling} onKeyUp={e => e.key === 'Enter' ? apiCall() : undefined} />
                </Grid2>
                <Grid2 size={'auto'} display={'flex'} justifyContent={'center'}>
                    <IconButton onClick={apiCall} disabled={apiCalling || !message} color="primary">
                        {apiCalling ? <CircularProgress size={24} /> : <Send />}
                    </IconButton>
                </Grid2>
            </Grid2>
        </Grid2>
    )
}
